import {
  init as amplitudeInit,
  track as amplitudeTrackEvent,
  getSessionId,
  setUserId
} from '@amplitude/analytics-browser';
import endpoints from '../../ajio-util/endpoints';
import {
  getAffiliateFeatureConfig,
  getAffiliateAttributionWindow,
  getCreatorProgramConfig
} from '../../../refresh/selectors/featureFlagsSelector';
import { getAffiliateCookie, setAffiliateCookie } from '../../../utils/affiliateUtils';

const amplitudeApiKey = endpoints.getProperties('amplitude_api_key');
const AMPLITUDE_SESSION_TIMEOUT = 30 * 60 * 1000; // 30 min
const SESSION_UTM_KEY = 'amplitude_session_utm';

let isInitialized = false;

const isBrowser = () =>
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof document.cookie === 'string';

const AFFILIATE_KEYS = [
    'attributed_affiliate_name',
    'attributed_affiliate_id',
    'attributed_affiliate_campaign_id',
    'attributed_affiliate_click_id',
    'attributed_affiliate_sub_id',
    'attributed_affiliate_additional_publisher_info',
    'attributed_affiliate_campaign_name',
    'attributed_affiliate_campaign_medium'
  ];

if(isBrowser()){
  const isAffiliateFeatureEnabled = getAffiliateFeatureConfig(window?.__PRELOADED_STATE__);
  const affiliateAttributionWindow = getAffiliateAttributionWindow(window?.__PRELOADED_STATE__);
  const isCreatorFeatureEnabled = getCreatorProgramConfig(window?.__PRELOADED_STATE__);
  console.log(
    'Affiliate logs: [amplitude.js][module-init] affiliateFeatureConfig:',
    isAffiliateFeatureEnabled
  );
  console.log(
    'Affiliate logs: [amplitude.js][module-init] creatorFeatureConfig:',
    isCreatorFeatureEnabled
  );
  console.log(
    'Affiliate logs: [amplitude.js][module-init] attributionWindow(hours):',
    affiliateAttributionWindow
  );
  const affiliateConfigForCookie = {
    ...(isAffiliateFeatureEnabled || {}),
    attributionWindow: affiliateAttributionWindow
  };
  console.log(
    'Affiliate logs: [amplitude.js][module-init] calling setAffiliateCookie, creatorEnabled:',
    !!isCreatorFeatureEnabled?.masterFlag
  );
  if (isCreatorFeatureEnabled?.masterFlag) {
    setAffiliateCookie(affiliateConfigForCookie, isCreatorFeatureEnabled);
  } else {
    setAffiliateCookie(affiliateConfigForCookie, {});
  }
}

const parseUTMParams = () => {
  let search = window.location.search || '';
  search = search.startsWith('?') ? search.slice(1) : search;
  const params = {};
  search.split('&').forEach(item => {
    const [key, value] = item.split('=');
    if (key) params[decodeURIComponent(key)] = value ? decodeURIComponent(value) : '';
  });
  return params;
};

const NA = 'NA';

const ANDROID_BRANDS = {
  samsung: /SM-|GT-|Samsung|Galaxy/i,
  Xiaomi: /Redmi|POCO|Mi\s|Xiaomi/i,
  OnePlus: /OnePlus/i,
  Google: /Pixel/i,
  Realme: /Realme|RMX\d+/i,
  Oppo: /Oppo|CPH\d+/i,
  Vivo: /Vivo/i,
  Motorola: /Moto|Motorola/i,
  Nokia: /Nokia/i,
  Huawei: /Huawei|Honor/i,
  Lenovo: /Lenovo/i,
  Infinix: /Infinix/i,
  Tecno: /Tecno/i,
  iQOO: /IQOO/i,
};

const getDeviceProps = () => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const base = { app_version: NA, carrier: NA };

  if (/iPad/i.test(ua)) return { ...base, device_brand: 'Apple', device_manufacturer: 'Apple', device_model: 'iPad' };
  if (/iPod/i.test(ua)) return { ...base, device_brand: 'Apple', device_manufacturer: 'Apple', device_model: 'iPod' };
  if (/iPhone/i.test(ua)) return { ...base, device_brand: 'Apple', device_manufacturer: 'Apple', device_model: 'iPhone' };

  if (/Android/i.test(ua)) {
    const modelMatch = ua.match(/Android\s+[\d.]+;\s*([^;)]+?)(?:\s+Build\/|\)|;)/i);
    const model = modelMatch ? modelMatch[1].trim() : NA;
    const brand = Object.keys(ANDROID_BRANDS).find(b => ANDROID_BRANDS[b].test(ua)) || NA;
    return { ...base, device_brand: brand, device_manufacturer: brand, device_model: model };
  }

  return { ...base, device_brand: NA, device_manufacturer: NA, device_model: NA };
};

let referralUserProps = { successful_referrals: NA };

export const setAmplitudeReferralProps = (props = {}) => {
  if (props.successful_referrals !== undefined && props.successful_referrals !== '') {
    referralUserProps = { successful_referrals: props.successful_referrals };
  }
};

const getReferralProps = () => ({ ...referralUserProps });



const getAffiliateProps = () => {
  const affiliateData = getAffiliateCookie();
  console.log('Affiliate logs: [amplitude.js][getAffiliateProps] cookie:', affiliateData);
  // 🔥 ALWAYS reset affiliate traits (even if no affiliateData present)
  if (!affiliateData) {
    const affiliate = {};
    AFFILIATE_KEYS.forEach(key => {
      affiliate[key] = 'NA';
    });
    console.log(
      'Affiliate logs: [amplitude.js][getAffiliateProps] no cookie -> NA traits:',
      affiliate
    );
    return affiliate;
  }
  console.log(
    'Affiliate logs: [amplitude.js][getAffiliateProps] returning cookie data:',
    affiliateData
  );
  return affiliateData;
};

const getSubscriptionAndSavingsProps = () => {
  let state = null;
  if (typeof window !== 'undefined') {
    try {
      state = window.store?.getState?.() || window.__PRELOADED_STATE__ || null;
    } catch (e) {
      state = window.__PRELOADED_STATE__ || null;
    }
  }
  const membershipData = state?.membership?.membershipData || {};
  const detail = membershipData.detail || {};
  const userDetail = membershipData.userDetail || {};
  const savingBreakup = userDetail.savingBreakup || {};
  return {
    subscription_status: membershipData.status ?? NA,
    subscription_version: detail.identifier ?? NA,
    subscription_id: detail.productId ?? NA,
    subscripiton_number: NA,
    subscription_start_date: userDetail.boughtOn ?? NA,
    subscription_end_date: userDetail.validTill ?? NA,
    subscribed_orders_left: userDetail.orderLeft ?? NA,
    subscription_first_purchase_date: NA,
    total_account_savings: userDetail.totalSavings ?? NA,
    platform_account_savings: savingBreakup.platformFeeSaving ?? NA,
    delivery_account_savings: savingBreakup.deliveryFeeSaving ?? NA,
    membership_discount_account_savings: savingBreakup.membershipSaving ?? NA,
  };
};

const SESSION_UTM_MAX_AGE = 30 * 60; // seconds

const setCookie = (name, value, maxAgeSeconds) => {
  try {
    const secure = typeof window !== 'undefined' && window.location?.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  } catch {}
};

const getCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

const removeCookie = (name) => {
  try {
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
  } catch {}
};

// Stored in a cookie so the captured UTM survives across tabs within the
// Amplitude session window. Expiry is rolling: every successful read
// re-sets the cookie with a fresh 30-min max-age, so the entry only drops
// after a full 30 min of inactivity (no events read it).
const storeSessionUTM = (fields) => {
  setCookie(SESSION_UTM_KEY, JSON.stringify(fields), SESSION_UTM_MAX_AGE);
};

const readSessionUTM = () => {
  const raw = getCookie(SESSION_UTM_KEY);
  if (!raw) return null;
  try {
    const fields = JSON.parse(raw);
    // Rolling 30-min inactivity: any activity that reads the session UTM
    // extends the window. After 30 min with no reads, the cookie expires
    // and the next event falls back to NA.
    storeSessionUTM(fields);
    return fields;
  } catch {
    removeCookie(SESSION_UTM_KEY);
    return null;
  }
};

const NA_SESSION_FIELDS = {
  session_source: 'NA',
  session_medium: 'NA',
  session_campaign_id: 'NA',
  session_campaign_name: 'NA',
  session_campaign_ad_id: 'NA',
  session_campaign_ad_name: 'NA',
  session_campaign_adset_id: 'NA',
  session_campaign_adset_name: 'NA',
  session_pid: 'NA',
  session_offer_id: 'NA',
  session_click_id: 'NA'
};

// `allowUrlCapture` is true only on page-load init. Subsequent per-event calls
// must read from storage only — otherwise the URL bar (which still holds the
// entry UTM until the user navigates) would override the inactivity expiry on
// every event and the rolling timeout would never actually fire.
// On init, fresh UTMs in the URL take precedence and overwrite the stored cookie
// so a new campaign visit reattributes the session instead of being ignored.
const captureSessionUTM = (allowUrlCapture = false) => {
  if (allowUrlCapture) {
    const params = parseUTMParams();
    const hasUTM = !!(params.utm_source || params.utm_medium || params.utm_campaign);
    if (hasUTM) {
      const trim = v => (typeof v === 'string' ? v.trim() : '');
      const utmFields = {
        session_source: trim(params.utm_source) || 'NA',
        session_medium: trim(params.utm_medium) || 'NA',
        session_campaign_id: trim(params.utm_campaignID) || 'NA',
        session_campaign_name: trim(params.utm_campaign) || 'NA',
        session_campaign_ad_id: trim(params.utm_adID) || 'NA',
        session_campaign_ad_name: trim(params.utm_adcontent) || 'NA',
        session_campaign_adset_id: trim(params.utm_adsetID) || 'NA',
        session_campaign_adset_name: trim(params.utm_term) || 'NA',
        session_pid: trim(params.pid) || 'NA',
        session_offer_id: trim(params.offer_id) || 'NA',
        session_click_id: trim(params.clickid) || 'NA'
      };
      storeSessionUTM(utmFields);
      return utmFields;
    }
  }

  const stored = readSessionUTM();
  if (stored) return stored;

  return { ...NA_SESSION_FIELDS };
};

const initAmplitude = () => {
  if (isInitialized || !amplitudeApiKey || !isBrowser()) return;
  amplitudeInit(amplitudeApiKey, {
    defaultTracking: {
      sessions: true, // equivalent to +sessions
      pageViews: false, // you're tracking manually
      formInteractions: false,
      fileDownloads: false,
      attribution: false
    },
    sessionTimeout: AMPLITUDE_SESSION_TIMEOUT, // new session after 30 min inactivity
    flushQueueSize: 1, // send each event immediately, one at a time
    flushIntervalMillis: 0, // no batching delay
    minIdLength: 1
  });
  captureSessionUTM(true);
  isInitialized = true;
};

if (isBrowser()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAmplitude();
    });
  } else {
    initAmplitude();
  }
}

export const amplitudeTrack = (eventName, eventProperties = {}, userId = '', deviceId = '', context = {} ) => {
  if (!isBrowser() || !amplitudeApiKey) return;
  initAmplitude();
  if (userId) setUserId(userId);

  const affiliateProps = getAffiliateProps();
  console.log(
    'Affiliate logs: [amplitude.js][amplitudeTrack] event:',
    eventName,
    'affiliateProps:',
    affiliateProps
  );
  const eventOptions = {
    ...(deviceId && { device_id: deviceId }),
    ...(context.userAgent && { user_agent: context.userAgent }),
    ...(context.locale && { language: context.locale }),
    ...(context.userAgentData?.platform && { platform: context.userAgentData.platform }),
    user_properties: {
      ...captureSessionUTM(),
      ...getSubscriptionAndSavingsProps(),
      ...getDeviceProps(),
      ...getReferralProps(),
      ...affiliateProps
    }
  };

  const pageProps = context.page ?  {
    '[Amplitude] Page Domain': window?.location?.hostname,
    '[Amplitude] Page Location': window?.location?.href,
    '[Amplitude] Page Path': window?.location?.pathname,
    '[Amplitude] Page Title': document?.title,
    '[Amplitude] Page URL': window?.location?.href,
    '[Amplitude] Previous Page Location': document?.referrer,
    '[Amplitude] Previous Page Type': document?.referrer ? 'referral' : 'direct',
  } : {};

  amplitudeTrackEvent(
    eventName,
    {
      ...pageProps,
      ...eventProperties,
      session_id: getSessionId()
    },
    eventOptions
  );
};

export const amplitudeIdentify = (userId = '', traits = {}) => {
  if (!isBrowser() || !amplitudeApiKey) return;
  initAmplitude();
  setUserId(userId || undefined);
  const affiliateProps = getAffiliateProps();
  console.log(
    'Affiliate logs: [amplitude.js][amplitudeIdentify] userId:',
    userId,
    'affiliateProps:',
    affiliateProps,
    'traits:',
    traits
  );
  amplitudeTrackEvent(
    'identify',
    {},
    {
      user_properties: {
        ...captureSessionUTM(),
        ...getSubscriptionAndSavingsProps(),
        ...getDeviceProps(),
        ...getReferralProps(),
        ...affiliateProps,
        ...traits
      }
    }
  );
};
 